import React, { useEffect, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Landing from './pages/Landing';
import Reviewers from './pages/Reviewers';
import Upload from './pages/Upload';
import Cafe from './pages/Cafe';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ReviewerDashboard from './pages/ReviewerDashboard';
import BecomeReviewer from './pages/BecomeReviewer';
// Lazy load admin dashboard - only loaded when admins access /admin routes
const AdminDashboard = lazy(() => import('./pages/AdminDashboardV2'));
import AdminLogin from './pages/AdminLogin';
import AdminChangePassword from './pages/AdminChangePassword';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminResetPassword from './pages/AdminResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import AdminSetup2FA from './pages/AdminSetup2FA';
import CopyPromptPage from './pages/CopyPromptPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CodeOfConduct from './pages/CodeOfConduct';
import ContactSupport from './pages/ContactSupport';
import SessionDashboard from './pages/SessionDashboard';
import PublicReviewerProfile from './pages/PublicReviewerProfile';
import ReviewerPublicPage from './pages/ReviewerPublicPage';
import ArtistComingSoon from './pages/ArtistComingSoon';
import StripeComplete from './pages/StripeComplete';
import StripeRefresh from './pages/StripeRefresh';
import LeaderboardPage from './pages/LeaderboardPage';
import GoLiveGuide from './pages/GoLiveGuide';
import ScrollToTop from './components/ScrollToTop';
import BetaAnnouncementBar from './components/BetaAnnouncementBar';
import { Toaster } from './components/ui/toaster';
import { initAuth } from './utils/auth';
import { trackEvent } from './utils/analytics';
import { useVisitorTracking } from './hooks/useVisitorTracking';

// Loading fallback for lazy-loaded admin dashboard
const AdminLoadingFallback = () => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F2D857] mx-auto mb-4"></div>
      <p className="text-gray-400">Loading Admin Dashboard...</p>
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-8">
          <div className="max-w-2xl text-center">
            <h1 className="text-4xl font-bold text-[#F2D857] mb-4">Oops! Something went wrong</h1>
            <p className="text-white mb-4">We encountered an error loading this page.</p>
            <p className="text-gray-400 text-sm mb-6">{this.state.error?.message}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-[#F2D857] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#F2D857]/90 transition"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to track page views on route changes
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on every route change
    trackEvent('page_view', {
      page_url: location.pathname,
      page_title: document.title
    });
  }, [location.pathname]);
  
  return null;
}

function App() {
  // Clean legacy avatar URLs from localStorage on app startup
  useEffect(() => {
    initAuth();
  }, []);

  // Track visitor engagement (fires after 10 seconds on site)
  useVisitorTracking();

  return (
    <BrowserRouter>
      <PageViewTracker />
      <ScrollToTop />
      {/* Beta Announcement Bar - Shows on all pages */}
      <BetaAnnouncementBar 
        showOnAllPages={true}
        animation="marquee"
        dismissDuration={7}
      />
      <div className="App min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/reviewers" element={<Reviewers />} />
            <Route path="/find-reviewers" element={<Navigate to="/reviewers" replace />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/upload/:reviewerUsername" element={<Upload />} />
            <Route path="/submit/:reviewerUsername" element={<Upload />} />
            <Route path="/cafe" element={<Cafe />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            {/* Redirect old /dashboard to reviewer-dashboard */}
            <Route path="/dashboard" element={<Navigate to="/reviewer-dashboard" replace />} />
            <Route path="/reviewer-dashboard" element={<ReviewerDashboard />} />
            {/* Redirect old /queue and /settings to reviewer-dashboard */}
            <Route path="/queue" element={<Navigate to="/reviewer-dashboard" replace />} />
            <Route path="/settings" element={<Navigate to="/reviewer-dashboard" replace />} />
            <Route path="/become-reviewer" element={<BecomeReviewer />} />
            <Route path="/become-a-reviewer" element={<BecomeReviewer />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Admin Back Office Routes - Separate from main app */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/change-password" element={<AdminChangePassword />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/accept-invite" element={<AcceptInvite />} />
            <Route path="/admin/setup-2fa" element={<AdminSetup2FA />} />
            <Route path="/admin/dashboard" element={
              <Suspense fallback={<AdminLoadingFallback />}>
                <AdminDashboard />
              </Suspense>
            } />
            <Route path="/admin/copy-prompt" element={<CopyPromptPage />} />
            <Route path="/admin" element={
              <Suspense fallback={<AdminLoadingFallback />}>
                <AdminDashboard />
              </Suspense>
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/conduct" element={<CodeOfConduct />} />
            <Route path="/support" element={<ContactSupport />} />
            
            {/* Stripe Connect Routes */}
            <Route path="/stripe/complete" element={<StripeComplete />} />
            <Route path="/stripe/refresh" element={<StripeRefresh />} />
            
            {/* March Madness Challenge Leaderboard */}
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            
            {/* Go Live Guide - Streaming setup reference page */}
            <Route path="/go-live-guide" element={<GoLiveGuide />} />
            
            {/* Reviewer-Scoped Public Session Route */}
            <Route path="/r/:reviewerSlug/submit" element={
              <ErrorBoundary>
                <ReviewerPublicPage />
              </ErrorBoundary>
            } />
            
            <Route path="/session/:sessionId" element={
              <ErrorBoundary>
                <SessionDashboard />
              </ErrorBoundary>
            } />
            <Route path="/reviewer/:username" element={<PublicReviewerProfile />} />
            <Route path="/r/:username" element={<PublicReviewerProfile />} />
            <Route path="/artist-coming-soon" element={<ArtistComingSoon />} />
            <Route path="/artist-waitlist" element={<ArtistComingSoon />} />
          </Routes>
        </main>
        <Footer />
        <Toaster />
      </div>
    </BrowserRouter>
  );
}

export default App;